import gql from "graphql-tag"
import { stripTypename } from "@coast/utils"
import { PackageTag, CourseType } from "./types"
import clientNext from "./ApolloClientNext"
import {
  GQLProductPackageFieldsFragment,
  GQLProductPackageWithPrice,
  Scalars,
} from "./graphqlTypes"
import { GetPackagesDocument } from "./packages.graphqlTypes"

export function includesRoadTest(
  productPackage: Omit<GQLProductPackageFieldsFragment, "__typename">,
) {
  return (
    productPackage.hasLessons &&
    !productPackage.hasCourse &&
    !!productPackage.lessonPackage?.numRoadTests &&
    !productPackage.lessonPackage?.isSharedLessons
  )
}

export function isRoadTestOnly(
  productPackage: Omit<GQLProductPackageFieldsFragment, "__typename">,
) {
  return (
    includesRoadTest(productPackage) &&
    productPackage.lessonPackage?.numLessons === 0
  )
}

export function transformProductPackageToPackageWithPrices(
  productPackage: Omit<GQLProductPackageFieldsFragment, "__typename">,
  discountAmount: number,
  msrp: number,
  price: number,
  couponDiscountAmount?: number,
): GQLProductPackageWithPrice {
  return {
    banner: productPackage.banner ?? null,
    couponDiscountAmount: couponDiscountAmount ?? null,
    courseId: productPackage.courseId ?? null,
    courseType: productPackage.courseType ?? null,
    description: productPackage.description,
    didApplyCoupon: !!couponDiscountAmount,
    discountAmount,
    extras: productPackage.tags ? productPackage.tags.join(", ") : null,
    hasCertificateShipping: productPackage.hasCertificateShipping ?? null,
    hasCourse: productPackage.hasCourse,
    hasLessons: productPackage.hasLessons,
    isHomePickup: productPackage.isHomePickup,
    id: productPackage.id as Scalars["String"],
    lessonPackage: productPackage.lessonPackage
      ? stripTypename(productPackage.lessonPackage)
      : null,
    msrp,
    price,
    state: productPackage.state,
    title: productPackage.title,
  }
}

gql`
  fragment ProductPackageFields on ProductPackage {
    id
    title
    banner
    description
    lessonPackage {
      id
      totalMinutes
      drivingMinutes
      observationMinutes
      numLessons
      numRoadTests
      isSharedLessons
    }
    courseId
    courseType
    hasCourse
    hasLessons
    isHomePickup
    state
    doHighlight
    hasCertificateShipping
    tags
  }

  query GetPackages($state: String!, $courseType: String) {
    packages(query: { courseType: $courseType, state: $state }) {
      ...ProductPackageFields
    }
  }
`

export const fetchPackages = async (
  state: string,
  courseType: CourseType | null = null,
): Promise<GQLProductPackageFieldsFragment[]> => {
  const response = await clientNext.query({
    query: GetPackagesDocument,
    variables: { state, courseType },
  })

  return response.data.packages.filter(
    (pkg): pkg is GQLProductPackageFieldsFragment => pkg !== null,
  )
}

const TOP_PACKS_BY_LESSON_TYPE: { [key: string]: string[] } = {
  standard: ["standard-6", "standard-6-home-pickup", "standard-10"],
  "dmv-test": ["dmv-35", "dmv-55", "dmv-95"],
  "shared-only": ["7x7", "11x7", "6x6"],
  shared: ["7x7-de", "7x7", "11x7", "6x6-de"],
  "road-test": ["road-test"],
  oneHour: ["one-hour", "8-one-hour", "6x6-de"],
}

export const sortPackagesByPopular = (
  packages: GQLProductPackageWithPrice[],
  lessonType: string,
): GQLProductPackageWithPrice[] => {
  /* For use a marketing strategy to choose what packages we want front and center
   * First sort by price for cleanness.
   * then use a filter or lessonType to choose the packages we want to display at the top
   */
  packages.sort((a, b) => a.price - b.price)
  if (lessonType in TOP_PACKS_BY_LESSON_TYPE) {
    const topDisplayPackages = packages.filter((p) =>
      TOP_PACKS_BY_LESSON_TYPE[lessonType].includes(p.id),
    )
    const otherPackages = packages.filter(
      (p) => !TOP_PACKS_BY_LESSON_TYPE[lessonType].includes(p.id),
    )
    return topDisplayPackages.concat(otherPackages)
  }

  // a catch all for new filters that aren't our main lesson types(i.e. course, all)
  const popular = packages.filter((p) => p.tags?.includes(PackageTag.Popular))
  const rest = packages.filter((p) => !p.tags?.includes(PackageTag.Popular))
  return popular.concat(rest)
}

export const filterPackages = (
  packages: GQLProductPackageWithPrice[],
  packageFilter: string,
): GQLProductPackageWithPrice[] =>
  packages.filter((pack) => {
    if (packageFilter === "all") return true
    if (packageFilter === "standard" || packageFilter === "oneHour") {
      return (
        pack.hasLessons &&
        !pack.lessonPackage?.numRoadTests &&
        !pack.lessonPackage?.isSharedLessons &&
        !pack.hasCourse
      )
    }
    if (
      packageFilter === "dmv-test" ||
      packageFilter.includes("road-test") ||
      packageFilter.includes("prep-test")
    ) {
      return (
        pack.hasLessons &&
        !pack.hasCourse &&
        !!pack.lessonPackage?.numRoadTests &&
        !pack.lessonPackage?.isSharedLessons
      )
    }
    if (packageFilter === "shared-only") {
      return (
        pack.hasLessons &&
        pack.lessonPackage?.isSharedLessons &&
        !pack.hasCourse
      )
    }
    if (packageFilter === "shared") {
      return pack.hasLessons && pack.lessonPackage?.isSharedLessons
    }
    if (packageFilter === "private") {
      // same as standard but w/ course packs
      return (
        pack.hasLessons &&
        !pack.lessonPackage?.numRoadTests &&
        !pack.lessonPackage?.isSharedLessons
      )
    }
    if (packageFilter === "course") {
      return pack.hasCourse
    }
    return false
  })

export type PackageCategories = {
  [key: string]: {
    id: string
    label: string
    description?: string
  }
}

export const PACKAGE_CATEGORIES: PackageCategories = {
  private: {
    id: "private",
    label: "Private Lessons",
  },
  shared: {
    id: "shared",
    label: "Shared Lessons",
  },
  "dmv-test": {
    id: "dmv-test",
    label: "Road Test",
  },
  "road-test": {
    id: "road-test",
    label: "Road Test",
  },
  "road-test-with-coastline-car": {
    id: "road-test-with-coastline-car",
    label: "Road Test with Coastline Car",
  },
  "road-test-15": {
    id: "road-test-15",
    label: "Road Test",
  },
  "road-test-15-with-coastline-car": {
    id: "road-test-15-with-coastline-car",
    label: "Road Test with Coastline Car",
  },
  course: {
    id: "course",
    label: "Courses",
  },
  standard: {
    id: "standard",
    label: "Standard Lessons",
  },
  "prep-test-60": {
    id: "prep-test-60",
    label: "Road Skills Test Prep + Road Test",
  },
  "road-test-120": {
    id: "road-test-120",
    label: "Road Test",
  },
  "road-test-180": {
    id: "road-test-180",
    label: "Prep + Road Test",
  },
}

type StateFilters = {
  [key: string]: string[]
}

export const STATE_FILTERS: StateFilters = {
  CA: ["private", "dmv-test", "course"],
  CO: ["private", "course"],
  IL: ["private", "road-test", "course"],
  IN: ["private", "road-test", "course"],
  MO: ["private", "course"],
  NV: ["private", "road-test", "course"],
  TX: ["private", "shared", "road-test", "course"],
  PA: ["private", "road-test-120"],
  DEFAULT: ["private", "road-test", "course"],
}

export const getStandardPackages = (
  packages: GQLProductPackageFieldsFragment[],
): GQLProductPackageFieldsFragment[] =>
  packages.filter(
    (productPackage) =>
      productPackage.hasLessons &&
      !productPackage.hasCourse &&
      !productPackage.lessonPackage?.numRoadTests &&
      !productPackage.lessonPackage?.isSharedLessons,
  )

export const getDmvPackages = (
  packages: GQLProductPackageFieldsFragment[],
): GQLProductPackageFieldsFragment[] => packages.filter(includesRoadTest)

export const getSharedPackages = (
  packages: GQLProductPackageFieldsFragment[],
): GQLProductPackageFieldsFragment[] =>
  packages.filter(
    (productPackage) =>
      productPackage.hasLessons &&
      !productPackage.hasCourse &&
      !!productPackage.lessonPackage?.isSharedLessons,
  )

export const getRoadTestPackages = (
  packages: GQLProductPackageFieldsFragment[],
  durationMinutes: number,
): GQLProductPackageFieldsFragment[] =>
  packages.filter(
    (productPackage) =>
      includesRoadTest(productPackage) &&
      productPackage.lessonPackage?.drivingMinutes === durationMinutes &&
      !productPackage.id.includes("with-coastline-car"),
  )

export const getRoadTestWithCarPackages = (
  packages: GQLProductPackageFieldsFragment[],
  durationMinutes: number,
): GQLProductPackageFieldsFragment[] =>
  packages.filter(
    (productPackage) =>
      includesRoadTest(productPackage) &&
      productPackage.lessonPackage?.drivingMinutes === durationMinutes &&
      productPackage.id.includes("with-coastline-car"),
  )

export const getPopularPackages = (
  packages: GQLProductPackageFieldsFragment[],
): GQLProductPackageFieldsFragment[] =>
  packages.filter((productPackage) =>
    productPackage.tags?.includes(PackageTag.Popular),
  )

export const getHomePickupPackages = (
  packages: GQLProductPackageFieldsFragment[],
): GQLProductPackageFieldsFragment[] =>
  packages.filter((productPackage) => productPackage.isHomePickup)

export const getMeetingPointPackages = (
  packages: GQLProductPackageFieldsFragment[],
): GQLProductPackageFieldsFragment[] =>
  packages.filter(
    (productPackage) =>
      productPackage.hasLessons && !productPackage.isHomePickup,
  )
